import React, { FC, useState } from 'react';
import { Button } from '@design-system/button';
import { Container } from '@design-system/container';
import { Typography } from '@design-system/typography';
import classNames from 'classnames';

import useJoiValidation from '../../hooks/useJoiValidation';
import { customProductVariantsSectionSchema } from './CustomProductVariants.schema';
import { ICustomProductVariantsSectionProps } from './models';

import './CustomProductVariants.scss';

const CustomProductVariants: FC<ICustomProductVariantsSectionProps> = ({
  variantStyle,
  title,
  text,
  scentsTitle,
  scentsList,
  packsTitle,
  packsList,
  imageScentOnePackOne,
  imageScentOnePackTwo,
  imageScentOnePackThree,
  imageScentTwoPackOne,
  imageScentTwoPackTwo,
  imageScentTwoPackThree,
  retailersTitle,
  retailersScentOnePackOne,
  retailersScentOnePackTwo,
  retailersScentOnePackThree,
  retailersScentTwoPackOne,
  retailersScentTwoPackTwo,
  retailersScentTwoPackThree,
}) => {
  const {
    data,
  }: {
    data: ICustomProductVariantsSectionProps;
  } = useJoiValidation(customProductVariantsSectionSchema, 'CustomProductVariantsSection', {
    variantStyle,
    title,
    text,
    scentsTitle,
    scentsList,
    packsTitle,
    packsList,
    imageScentOnePackOne,
    imageScentOnePackTwo,
    imageScentOnePackThree,
    imageScentTwoPackOne,
    imageScentTwoPackTwo,
    imageScentTwoPackThree,
    retailersTitle,
    retailersScentOnePackOne,
    retailersScentOnePackTwo,
    retailersScentOnePackThree,
    retailersScentTwoPackOne,
    retailersScentTwoPackTwo,
    retailersScentTwoPackThree,
  });

  const scents = scentsList ? JSON.parse(scentsList) : '';
  const packs = packsList ? JSON.parse(packsList) : '';

  const imageOneOne = imageScentOnePackOne ? JSON.parse(imageScentOnePackOne) : '';
  const retailerOneOne = retailersScentOnePackOne ? JSON.parse(retailersScentOnePackOne) : '';
  const imageOneTwo = imageScentOnePackTwo ? JSON.parse(imageScentOnePackTwo) : '';
  const retailerOneTwo = retailersScentOnePackTwo ? JSON.parse(retailersScentOnePackTwo) : '';
  const imageOneThree = imageScentOnePackThree ? JSON.parse(imageScentOnePackThree) : '';
  const retailerOneThree = retailersScentOnePackThree ? JSON.parse(retailersScentOnePackThree) : '';
  const imageTwoOne = imageScentTwoPackOne ? JSON.parse(imageScentTwoPackOne) : '';
  const retailerTwoOne = retailersScentTwoPackOne ? JSON.parse(retailersScentTwoPackOne) : '';
  const imageTwoTwo = imageScentTwoPackTwo ? JSON.parse(imageScentTwoPackTwo) : '';
  const retailerTwoTwo = retailersScentTwoPackTwo ? JSON.parse(retailersScentTwoPackTwo) : '';
  const imageTwoThree = imageScentTwoPackThree ? JSON.parse(imageScentTwoPackThree) : '';
  const retailerTwoThree = retailersScentTwoPackThree ? JSON.parse(retailersScentTwoPackThree) : '';

  const productOne = { ...imageOneOne, ...retailerOneOne };
  const productTwo = { ...imageOneTwo, ...retailerOneTwo };
  const productThree = { ...imageOneThree, ...retailerOneThree };
  const productFour = { ...imageTwoOne, ...retailerTwoOne };
  const productFive = { ...imageTwoTwo, ...retailerTwoTwo };
  const productSix = { ...imageTwoThree, ...retailerTwoThree };

  const products = [
    {
      scentName: scents[0],
      scentId: 1,
      packsList: [
        {
          packName: packs[0],
          packId: 1,
          product: productOne,
        },
        {
          packName: packs[1],
          packId: 2,
          product: productTwo,
        },
        {
          packName: packs[2],
          packId: 3,
          product: productThree,
        },
      ],
    },
    {
      scentName: scents[1],
      scentId: 2,
      packsList: [
        {
          packName: packs[0],
          packId: 1,
          product: productFour,
        },
        {
          packName: packs[1],
          packId: 2,
          product: productFive,
        },
        {
          packName: packs[2],
          packId: 3,
          product: productSix,
        },
      ],
    },
  ];

  function firstFilledPackId(currentScent: number): number {
    // eslint-disable-next-line no-nested-ternary
    return Object.keys(products[currentScent].packsList[0].product).length !== 0
      ? 0
      : Object.keys(products[currentScent].packsList[1].product).length !== 0
      ? 1
      : 2;
  }

  const [activeScentValue, setActiveScentValue] = useState(0);
  const [activePackValue, setActivePackValue] = useState(firstFilledPackId(0));

  const currentProduct = products[activeScentValue].packsList[activePackValue].product;

  return (
    <div
      className={classNames('custom-product-variants__section', {
        [`custom-product-variants__section-${variantStyle}`]: variantStyle,
      })}
    >
      <Container maxWidth="max-grid" className="custom-product-variants__container">
        <div className="custom-product-variants__product-container">
          <div className="custom-product-variants__content">
            {data.title ? (
              <Typography
                className="custom-product-variants__title"
                variant="headline"
                tag="h2"
                size="xxxl"
                content={data.title}
                wrapping="default"
                weight="bold"
              />
            ) : null}
            {data.text ? (
              <Typography
                className="custom-product-variants__text"
                variant="body"
                tag="p"
                size="m"
                content={data.text}
                wrapping="default"
              />
            ) : null}
          </div>
          <div className="custom-product-variants__image">
            <img src={currentProduct.imageUrl} alt={currentProduct.imageAlt} />
          </div>
          <div className="custom-product-variants__tabs">
            <div className="custom-product-variants__tabs-packs">
              {data.packsTitle ? (
                <Typography
                  className="custom-product-variants__tabs-packs-title"
                  variant="body"
                  tag="p"
                  size="s"
                  content={data.packsTitle}
                  wrapping="default"
                />
              ) : null}
              {packs
                ? packs.map((pack, i) =>
                    pack ? (
                      <Button
                        className={classNames('custom-product-variants__tabs-pack', {
                          [`custom-product-variants__tabs-pack-active`]: i === activePackValue,
                        })}
                        variant="primary"
                        key={packs[i]}
                        onClick={() => setActivePackValue(i)}
                        disabled={
                          Object.keys(products[activeScentValue].packsList[i].product).length === 0
                        }
                      >
                        {pack}
                      </Button>
                    ) : null
                  )
                : null}
            </div>
            <div className="custom-product-variants__tabs-scents">
              {data.scentsTitle ? (
                <Typography
                  className="custom-product-variants__tabs-scents-title"
                  variant="body"
                  tag="p"
                  size="s"
                  content={data.scentsTitle}
                  wrapping="default"
                />
              ) : null}
              {scents
                ? scents.map((scent, j) => (
                    <Button
                      className={classNames('custom-product-variants__tabs-scent', {
                        [`custom-product-variants__tabs-scent-active`]: j === activeScentValue,
                      })}
                      variant="primary"
                      key={scents[j]}
                      onClick={() => {
                        setActiveScentValue(j);
                        setActivePackValue(firstFilledPackId(j));
                      }}
                    >
                      {scent}
                    </Button>
                  ))
                : null}
            </div>
          </div>
          <div className="custom-product-variants__retailers">
            {data.retailersTitle ? (
              <Typography
                className="custom-product-variants__retailers-title"
                variant="body"
                tag="p"
                size="s"
                content={data.retailersTitle}
                wrapping="default"
              />
            ) : null}
            {currentProduct.retailersList?.map((retailer, i) =>
              retailer.retailerTitle ? (
                <div className="custom-product-variants__retailer" key={retailer[i]}>
                  <a
                    className="custom-product-variants__retailer-link"
                    href={retailer.retailerUrl}
                    title={retailer.retailerTitle}
                    aria-label={retailer.retailerTitle}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      src={retailer.retailerImage}
                      alt={retailer.retailerTitle}
                      className="custom-product-variants__retailer-image"
                    />
                  </a>
                </div>
              ) : null
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};
export default CustomProductVariants;
